import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"

import { getLiveLifeDashboardResultsV2 } from "../../../requesters/CalculatorResultsRequester"
import { sendLearnWorldAccessRequest } from "../../../requesters/UsersRequester"
import WellbeingIndexChart from "../WellbeingIndexChart"
import PqIndexGauge from "./PqIndexGauge"
import { OVERVIEW_PAGE_CONTENT } from "../constants"
import LiveLifeDashboardEmptyState from "./LiveLifeDashboardEmptyState"
import LiveLifeV2BreakdownCard from "./LifeLifeV2BreakdownCard"
import { getAssetPath } from "../../../utils/assets"

const toChartHistory = (history, valueKey) => (history || []).map(row => ({
  wellbeing_index: row[valueKey],
  completed_at_month: row.date
}))

function LiveLifeV2Dashboard({ baseUrl, currentUser }) {
  const [dashboard, setDashboard] = useState(null)
  const [inflight, setInflight] = useState(true)
  const [redirecting, setRedirecting] = useState(false)
  const unfinishedSurveyExists = currentUser.active_enrolment.is_new_attempt_live_life
  const isLearnWorld = currentUser.active_enrolment?.integration_type === "learn_world"

  useEffect(() => {
    document.title = "Performance Intelligence Academy | PQ Index Dashboard"
  }, [])

  // LearnWorlds participants review their PQ Index summary here first, then continue
  // to the course on their own via this button — completing the calculator no longer
  // redirects straight into the course.
  const goToCourse = () => {
    setRedirecting(true)
    sendLearnWorldAccessRequest(baseUrl, currentUser.program_id)
      .then(res => {
        const url = res.response?.status === "ok" && res.response.json?.learn_world?.url
        if (url) {
          window.location.href = url
        } else {
          setRedirecting(false)
          toast.error("Unable to open your course right now. Please try again.")
        }
        return res
      })
      .catch(() => {
        setRedirecting(false)
        toast.error("Unable to open your course right now. Please try again.")
      })
  }

  const fetchResults = useCallback(() => {
    getLiveLifeDashboardResultsV2(baseUrl, currentUser).then(res => {
      if (res?.response) {
        setDashboard(res.response)
      }
      setInflight(false)
      return null
    }).catch(err => {
      setInflight(false)
      // eslint-disable-next-line no-console
      console.log(err)
    })
  }, [baseUrl, currentUser])

  useEffect(() => {
    fetchResults()
  }, [fetchResults])

  if (inflight) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  // // Route to linking code page when User not enrolled in a program
  if (!currentUser?.active_enrolment) {
    window.location.href = "/overview"
  }

  // Render empty state page if no results are found
  if (!dashboard && !inflight) {
    return <LiveLifeDashboardEmptyState unfinishedSurveyExists={unfinishedSurveyExists} version="2" />
  }

  const pgScoreHistory = toChartHistory(dashboard?.pg_score_history, "pg_score")

  return (
    <div className="dashboard-body">
      <div className="content">
        <div className="container dashboard" id="dashboard-10dwc">
          <div className="d-flex align-items-center justify-content-between py-4">
            <div>
              <Link
                to="/overview"
                className="back-to-overview">
                <img src={getAssetPath("icn-back")} />
              </Link>
              <img src={getAssetPath(OVERVIEW_PAGE_CONTENT.live_life_v2.logoAssetName)} />
            </div>
            <div className="start-survey">
              <Link className="btn btn-main ml-4" to="/livelife/intro">{`${unfinishedSurveyExists ? "Start" : "Retake"} Survey`}</Link>
              {isLearnWorld && (
                <button
                  type="button"
                  className="btn btn-outline-main ml-4"
                  onClick={goToCourse}
                  disabled={redirecting}>
                  {redirecting ? "Opening your course…" : "Access PQ Academy"}
                </button>
              )}
              <div className="small">{`Last completed on ${dashboard?.completed_at}`}</div>
            </div>
          </div>
          <div className="dash-row row wellness-index">
            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <div className="pre-text">PQ Index</div>
                  <div className="gauge">
                    <PqIndexGauge score={dashboard?.pg_score} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <div className="pre-text">PQ Index Trend</div>
                  <WellbeingIndexChart results={pgScoreHistory} xAxisLabel="Assessment date" yAxisLabel="Score" />
                </div>
              </div>
            </div>
          </div>
          <div className="dash-row row">
            <div className="col-lg-4"><Link className="btn btn-lg-block bg-main" to="/about-livelife">What is PQ Index?</Link></div>
            {currentUser.active_enrolment?.live_life_latest_submitted_id && (
              <div className="col-lg-8">
                <a
                  className={`btn btn-lg-block btn-outline-main${dashboard?.download_pdf_url ? "" : " disabled"}`}
                  target="_blank"
                  rel="noreferrer"
                  href={dashboard?.download_pdf_url || undefined}
                  aria-disabled={!dashboard?.download_pdf_url}
                  tabIndex={dashboard?.download_pdf_url ? undefined : -1}
                  onClick={e => { if (!dashboard?.download_pdf_url) e.preventDefault() }}>
                  Download Latest Report
                </a>
              </div>
            )}
          </div>
          <LiveLifeV2BreakdownCard dashboard={dashboard} baseUrl={baseUrl} enrolment={currentUser.active_enrolment} />
        </div>
      </div>
    </div>
  )
}

export default LiveLifeV2Dashboard
